import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from 'next-themes';
import * as Sentry from '@sentry/react';
import App from './app/App';
import './styles/index.css';

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  });
}

// Self-heal a stale deploy: when a lazy route chunk fails to load — almost always because a new
// build shipped and this long-lived tab is still running the old one (its hashed chunks are gone) —
// reload ONCE to pull the fresh bundle. Users must never have to hard-refresh. Guarded against
// reload loops (won't retry within 10s) so a genuinely-down server doesn't thrash.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'plcy:chunk-reload-at';
  const last = Number(sessionStorage.getItem(KEY) ?? '0');
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(KEY, String(Date.now()));
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 5 * 60_000, retry: 1 },
  },
});

// App-wide error fallback: a component or lazy-chunk load error degrades to a recoverable screen
// instead of white-screening the whole SPA. Sits OUTSIDE App's <Suspense> so it also catches
// failed lazy imports.
function AppErrorFallback({ resetError }: { error: unknown; resetError: () => void }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, fontFamily: 'system-ui, sans-serif' }}>
      <div style={{ maxWidth: 420, textAlign: 'center' }}>
        <h1 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>Something went wrong</h1>
        <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 20 }}>
          This screen hit an unexpected error. Your session is safe — try again or reload.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button onClick={resetError} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #d1d5db', background: '#fff', cursor: 'pointer', fontSize: 14 }}>Try again</button>
          <button onClick={() => window.location.assign('/')} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: 14 }}>Reload app</button>
        </div>
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/* Force the light theme regardless of OS preference — the admin is light-only for now. */}
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} forcedTheme="light" disableTransitionOnChange>
      <QueryClientProvider client={queryClient}>
        <Sentry.ErrorBoundary fallback={AppErrorFallback}>
          <BrowserRouter>
            <Routes>
              <Route path="/:tab?" element={<App />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </Sentry.ErrorBoundary>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>,
);
